import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

const FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
];

export default function QrScanner({ open, onScan, onClose, continuous = false, scannedList = [] }) {
  const scannerRef = useRef(null);
  const containerRef = useRef(null);
  const lastScanned = useRef('');
  const lastTime = useRef(0);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null); // { code, ok }
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setTorchOn(false);
    setTorchAvailable(false);
    lastScanned.current = '';

    let scanner = null;
    const containerId = 'qr-reader-' + Date.now();

    const timer = setTimeout(async () => {
      try {
        if (!containerRef.current) return;
        containerRef.current.id = containerId;

        scanner = new Html5Qrcode(containerId, {
          formatsToSupport: FORMATS,
          verbose: false,
          // Détecteur natif du navigateur quand dispo (bien plus fiable/rapide)
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        });
        scannerRef.current = scanner;

        const onDecode = (text) => {
          const code = text.trim().toUpperCase();
          const now = Date.now();

          // Debounce: ignore same code within 3 seconds
          if (code === lastScanned.current && now - lastTime.current < 3000) return;
          lastScanned.current = code;
          lastTime.current = now;

          if (continuous) {
            // Continuous mode: don't stop scanner, just call onScan
            onScan(code);
            setFlash({ code, ok: true });
            setTimeout(() => setFlash(null), 800);
          } else {
            // Single mode: stop and close
            try { scanner.stop().catch(() => {}); scannerRef.current = null; } catch {}
            onScan(code);
          }
        };

        // Zone de scan large et adaptée à l'écran (mieux pour codes 1D et QR)
        const config = {
          fps: 12,
          qrbox: (vw, vh) => ({
            width: Math.max(200, Math.floor(Math.min(vw * 0.85, 480))),
            height: Math.max(180, Math.floor(Math.min(vh * 0.55, 360))),
          }),
        };

        // 1er essai : caméra arrière en haute résolution ('ideal' = jamais
        // bloquant par spec ; PAS de focusMode ici, certains téléphones
        // plantent au démarrage avec des contraintes 'advanced')
        try {
          await scanner.start(
            { facingMode: 'environment' },
            {
              ...config,
              videoConstraints: {
                facingMode: 'environment',
                width: { ideal: 1920 }, height: { ideal: 1080 },
              },
            },
            onDecode, () => {}
          );
        } catch (eHd) {
          // Repli : l'échec peut laisser la caméra dans un état bloqué →
          // on repart d'une instance NEUVE avec les réglages par défaut
          try { await scanner.stop(); } catch {}
          try { await scanner.clear(); } catch {}
          scanner = new Html5Qrcode(containerId, {
            formatsToSupport: FORMATS,
            verbose: false,
            experimentalFeatures: { useBarCodeDetectorIfSupported: true },
          });
          scannerRef.current = scanner;
          await scanner.start({ facingMode: 'environment' }, config, onDecode, () => {});
        }

        // Autofocus continu appliqué APRÈS démarrage (best effort, silencieux)
        try { await scanner.applyVideoConstraints({ advanced: [{ focusMode: 'continuous' }] }); } catch {}

        // Lampe torche si l'appareil la propose (utile en entrepôt)
        try {
          const caps = scanner.getRunningTrackCapabilities?.();
          if (caps && caps.torch) setTorchAvailable(true);
        } catch {}
      } catch (e) {
        console.error('Scanner error:', e);
        setError('Impossible d\'ouvrir la caméra. Vérifie les permissions.');
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [open]);

  async function toggleTorch() {
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      await scanner.applyVideoConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(v => !v);
    } catch (e) { console.warn('torch:', e?.message); }
  }

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 300, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: '#111', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ color: '#FFF', fontSize: 15, fontWeight: 700 }}>
          {continuous ? '📷 Scan continu' : '📷 Scanner'}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          {torchAvailable && (
            <button onClick={toggleTorch} style={{ background: torchOn ? '#B7791F' : '#333', border: 'none', color: '#FFF', fontSize: 14, cursor: 'pointer', padding: '6px 14px', borderRadius: 8, fontFamily: 'inherit', fontWeight: 600 }}>🔦 {torchOn ? 'ON' : 'OFF'}</button>
          )}
          <button onClick={onClose} style={{ background: '#333', border: 'none', color: '#FFF', fontSize: 14, cursor: 'pointer', padding: '6px 14px', borderRadius: 8, fontFamily: 'inherit', fontWeight: 600 }}>Fermer</button>
        </div>
      </div>

      {/* Flash feedback */}
      {flash && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          padding: '16px 32px', borderRadius: 16, fontSize: 24, fontWeight: 800,
          color: '#FFF', background: 'rgba(22, 163, 74, 0.9)', zIndex: 310,
          fontFamily: 'monospace', letterSpacing: 2,
        }}>✅ {flash.code}</div>
      )}

      {/* Camera view */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {error ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#F87171', fontSize: 14 }}>
            {error}
            <br /><br />
            <button onClick={onClose} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#FFF', color: '#000', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Fermer</button>
          </div>
        ) : (
          <div ref={containerRef} style={{ width: '100%', maxWidth: 500 }} />
        )}
      </div>

      {/* Scanned list (continuous mode) */}
      {continuous && scannedList.length > 0 && (
        <div style={{ background: '#111', padding: '8px 16px', maxHeight: '30vh', overflowY: 'auto', flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 4 }}>{scannedList.length} caisse{scannedList.length > 1 ? 's' : ''} scannée{scannedList.length > 1 ? 's' : ''}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {scannedList.map((item, i) => (
              <span key={i} style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 13, fontWeight: 700,
                fontFamily: 'monospace', background: item.ok ? '#166534' : '#991B1B', color: '#FFF',
              }}>{item.code}</span>
            ))}
          </div>
        </div>
      )}

      {/* Instruction */}
      <div style={{ background: '#111', padding: '10px 16px', textAlign: 'center', fontSize: 12, color: '#6B7280', flexShrink: 0 }}>
        Pointez la caméra vers le code-barres — scan automatique
      </div>
    </div>
  );
}
